import { deleteAnnouncementSeenCookies, sendApiRequest } from "./requestConfig";
import {
  ANNOUNCEMENT_FEED_PAGE_SIZE,
  extractAnnouncementPage,
  isAnnouncementAudienceQueryType,
  normalizeAnnouncement,
  normalizeAnnouncementType,
  serializeAnnouncementTypes,
  sortAnnouncementsByIdDesc,
} from "./announcementUtils";
import { startApiSubscription } from "./subscriptionRequest";

const ANNOUNCEMENTS_CHANGED_EVENT = "cap-announcements-changed";
const ANNOUNCEMENTS_SUBSCRIBE_PATH = "announcements/subscribe";

let announcementSubscriptionListenerCount = 0;
let stopAnnouncementsServerSubscription = null;
let legacyAnnouncementSeenStorageCleared = false;

const asSubscriptionEventType = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === "published" || normalized === "unpublished" || normalized === "unpublish") {
    return normalized;
  }
  return "";
};

export const announcementSubscriptionEventType = (payload) => {
  if (typeof payload === "string") {
    return asSubscriptionEventType(payload);
  }
  if (!payload || typeof payload !== "object") {
    return "";
  }

  const nested = payload.content && typeof payload.content === "object" ? payload.content : null;
  return (
    asSubscriptionEventType(payload.event) ||
    asSubscriptionEventType(payload.eventType) ||
    asSubscriptionEventType(payload.action) ||
    asSubscriptionEventType(nested?.event) ||
    asSubscriptionEventType(nested?.eventType) ||
    asSubscriptionEventType(payload.type) ||
    asSubscriptionEventType(nested?.type)
  );
};

export const emitAnnouncementsChanged = (detail) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(ANNOUNCEMENTS_CHANGED_EVENT, { detail: detail || {} }));
  }
};

export const startAnnouncementsSubscription = () =>
  startApiSubscription({
    apiPath: ANNOUNCEMENTS_SUBSCRIBE_PATH,
    onMessage: (payload) => {
      emitAnnouncementsChanged(payload);
    },
  });

const connectAnnouncementsServerSubscription = () => {
  if (stopAnnouncementsServerSubscription !== null || typeof window === "undefined") {
    return;
  }
  stopAnnouncementsServerSubscription = startAnnouncementsSubscription();
};

const disconnectAnnouncementsServerSubscription = () => {
  if (announcementSubscriptionListenerCount > 0 || stopAnnouncementsServerSubscription === null) {
    return;
  }
  stopAnnouncementsServerSubscription();
  stopAnnouncementsServerSubscription = null;
};

export const subscribeAnnouncementsChanged = (listener) => {
  if (typeof window === "undefined") {
    return () => {};
  }
  window.addEventListener(ANNOUNCEMENTS_CHANGED_EVENT, listener);
  announcementSubscriptionListenerCount += 1;
  connectAnnouncementsServerSubscription();
  return () => {
    window.removeEventListener(ANNOUNCEMENTS_CHANGED_EVENT, listener);
    announcementSubscriptionListenerCount = Math.max(0, announcementSubscriptionListenerCount - 1);
    disconnectAnnouncementsServerSubscription();
  };
};

const toNonNegativeInt = (value, fallback) => {
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric >= 0) {
    return numeric;
  }
  return fallback;
};

const ANNOUNCEMENT_SORT_FIELDS = ["id", "title", "type", "published", "published_timestamp", "published_by"];

const toSortField = (value) => {
  const field = String(value || "").trim();
  return ANNOUNCEMENT_SORT_FIELDS.indexOf(field) !== -1 ? field : "";
};

const toSortDirection = (value) => (value === "asc" ? "asc" : "desc");

const announcementsRequestPath = (options = {}) => {
  const params = [];
  const type = normalizeAnnouncementType(options.type);
  if (isAnnouncementAudienceQueryType(type)) {
    params.push("type=" + encodeURIComponent(type));
  }

  const search = typeof options.search === "string" ? options.search.trim() : "";
  if (search !== "") {
    params.push("search_term=" + encodeURIComponent(search));
  }

  const sortField = toSortField(options.sort_by);
  if (sortField !== "") {
    params.push("sort_by=" + encodeURIComponent(sortField));
    params.push("sort_dir=" + encodeURIComponent(toSortDirection(options.sort_dir)));
  }

  const page = toNonNegativeInt(options.page, 0);
  const sizeValue = toNonNegativeInt(options.size, ANNOUNCEMENT_FEED_PAGE_SIZE);
  const size = sizeValue > 0 ? sizeValue : ANNOUNCEMENT_FEED_PAGE_SIZE;
  params.push("page=" + encodeURIComponent(String(page)));
  params.push("size=" + encodeURIComponent(String(size)));

  return "announcements?" + params.join("&");
};

const requestAnnouncementList = async (silent, apiPath) =>
  silent === true
    ? await sendApiRequest(apiPath, {}, "GET", { silent: true })
    : await sendApiRequest(apiPath, {}, "GET");

export const fetchAnnouncements = async (silent = false, options) => {
  const requestOptions = options && typeof options === "object" ? options : {};
  const audienceType = normalizeAnnouncementType(requestOptions.type || requestOptions.audienceType);
  const page = toNonNegativeInt(requestOptions.page, 0);
  const sizeValue = toNonNegativeInt(requestOptions.size, ANNOUNCEMENT_FEED_PAGE_SIZE);
  const size = sizeValue > 0 ? sizeValue : ANNOUNCEMENT_FEED_PAGE_SIZE;
  const search = typeof requestOptions.search === "string" ? requestOptions.search : "";
  const sortField = toSortField(requestOptions.sort_by);
  const sortDir = toSortDirection(requestOptions.sort_dir);
  const result = await requestAnnouncementList(
    silent,
    announcementsRequestPath({
      type: audienceType,
      page,
      size,
      search,
      sort_by: sortField,
      sort_dir: sortDir,
    })
  );

  if (typeof result.success !== "undefined" && result.success === true) {
    const pageData = extractAnnouncementPage(result.response, { page, size });
    const list = pageData.list.map(normalizeAnnouncement);
    return {
      success: true,
      announcements: sortField !== "" ? list : sortAnnouncementsByIdDesc(list),
      totalElements: pageData.totalElements,
      totalPages: pageData.totalPages,
      number: pageData.number,
      size: pageData.size,
      numberOfElements: pageData.numberOfElements,
      last: pageData.last,
      hasMore: pageData.hasMore === true,
    };
  }

  return {
    success: false,
    announcements: [],
    totalElements: 0,
    totalPages: 0,
    number: page,
    size,
    numberOfElements: 0,
    last: true,
    hasMore: false,
    message: result.message || "Sorry, something went wrong",
  };
};

const toUnseenCount = (response) => {
  const asCount = (value) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 0) {
      return Math.floor(numeric);
    }
    return null;
  };

  if (typeof response === "number" || typeof response === "string") {
    return asCount(response);
  }

  if (response && typeof response === "object") {
    const candidates = [
      response.unseenCount,
      response.unseen_count,
      response.count,
      response.content,
      response.data,
      response.value,
    ];
    for (let index = 0; index < candidates.length; index += 1) {
      const candidate = candidates[index];
      if (typeof candidate === "number" || typeof candidate === "string") {
        const parsed = asCount(candidate);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
  }

  return null;
};

export const fetchUnseenCount = async () => {
  if (!legacyAnnouncementSeenStorageCleared) {
    deleteAnnouncementSeenCookies();
    legacyAnnouncementSeenStorageCleared = true;
  }
  const result = await sendApiRequest("announcements/unseen-count", {}, "GET", { silent: true });
  if (result.success === true) {
    const count = toUnseenCount(result.response);
    if (count !== null) {
      return {
        success: true,
        count,
      };
    }
  }

  return {
    success: false,
    count: 0,
  };
};

export const markAnnouncementSeen = async (announcementId, seen, signal) => {
  const result = await sendApiRequest(
    "announcements/seen/" + encodeURIComponent(announcementId),
    { seen: seen === true },
    "POST",
    { silent: true, signal }
  );
  return {
    success: result.success === true,
    aborted: result.aborted === true,
  };
};

export const markAllAnnouncementsSeen = async () => {
  const result = await sendApiRequest("announcements/seen/all", {}, "POST", { silent: true });
  return {
    success: result.success === true,
  };
};

export const saveAnnouncement = async (payload) => {
  const requestBody = payload && typeof payload === "object" ? { ...payload } : {};
  requestBody.type = serializeAnnouncementTypes(
    requestBody.type != null ? requestBody.type : requestBody.types
  );
  delete requestBody.types;
  const result = await sendApiRequest("announcements/save", requestBody, "POST");
  if (typeof result.success !== "undefined" && result.success === true) {
    return {
      success: true,
      announcement: normalizeAnnouncement(result.response),
    };
  }

  return {
    success: false,
    message: result.message || "Sorry, something went wrong",
  };
};

export const deleteAnnouncement = async (announcementId) => {
  const result = await sendApiRequest("announcements/delete/" + announcementId, {}, "POST");
  if (typeof result.success !== "undefined" && result.success === true) {
    return { success: true };
  }

  return {
    success: false,
    message: result.message || "Sorry, something went wrong",
  };
};
